import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { SmartSort, IntendedDisplay } from '@iheartjane/dm-sdk';
import { hideStrikethroughPrice } from '@iheartjane/menu';
import type {
  BannerCustomImageAdSlide,
  ProductCardPrice,
  WebCustomImageAdEvent,
} from '@iheartjane/menu';

import {
  getPriceBeforeDiscounts,
  getPriceIncludingDiscounts,
  getProductPriceLabels,
  hasSpecialApplied,
} from '@jane/bloom/viewModels/getPrice';

interface BuildCustomImageAdSlideArgs {
  instance: SmartSort<MaterializedMenuProduct>;
  intendedDisplay: IntendedDisplay;
  onEvent?: (event: WebCustomImageAdEvent) => void;
  onImageClick?: (brandName: string) => void;
  showPrice?: boolean;
  state?: string;
}

// mirrors the Bloom product card: discounted label in the deal color, original struck through
const getProductCardPrice = (
  attributes: MaterializedMenuProduct,
  state?: string
): ProductCardPrice => {
  // ad payloads only carry the attributes their API key is granted
  const product = {
    ...attributes,
    available_weights: attributes.available_weights ?? [],
  };
  const defaultWeight = product.available_weights[0] ?? 'each';
  const defaultPrice = getPriceIncludingDiscounts(product, defaultWeight);

  if (defaultPrice == null) return {};

  const { discountedPriceLabel, priceLabel } = getProductPriceLabels(product);
  const isDiscounted =
    Boolean(hasSpecialApplied(product)) &&
    defaultPrice !== getPriceBeforeDiscounts(product, defaultWeight);

  return {
    isDiscounted,
    originalPrice:
      isDiscounted && !hideStrikethroughPrice(state ?? '')
        ? priceLabel
        : undefined,
    price: discountedPriceLabel,
  };
};

export const buildCustomImageAdSlide = ({
  instance,
  intendedDisplay,
  onEvent,
  onImageClick,
  showPrice,
  state,
}: BuildCustomImageAdSlideArgs): BannerCustomImageAdSlide | undefined => {
  if (!instance.products.length) {
    return undefined;
  }

  const customImageData = instance.customImages.find(
    (image) => image.intendedDisplay === intendedDisplay
  );

  if (!customImageData) {
    return undefined;
  }

  return {
    customImageData,
    id: 'carousel-image-ad',
    kind: 'customImageAd',
    onEvent,
    onImageClick,
    products: instance.products.map((product) => {
      const { attributes } = product;

      return {
        aggregate_rating: attributes.aggregate_rating ?? 0,
        brand: attributes.brand ?? '',
        category: attributes.category ?? '',
        clickHandler: () => void product.click(),
        image_urls: attributes.image_urls ?? [],
        name: attributes.name,
        pdpUrl: `/products/${attributes.product_id}`,
        product_id: attributes.product_id,
        review_count: attributes.review_count ?? 0,
        ...getProductCardPrice(attributes, state),
      };
    }),
    showPrice,
  };
};
